import { useEffect, useState, useCallback, useMemo } from "react"
import Helpers from "../../../Helpers/Helpers"
import ProductCard from "../../components/card/ProductCard"
import { useLocation, useNavigate } from "react-router-dom"
import Pagination from "../../components/Pagination"
import BannerCarousel from "../../components/BannerCarousel"
import debounce from 'lodash/debounce'

const ShopComponent = ({ title }) => {
    const helpers = Helpers()
    const navigate = useNavigate()
    const { search } = useLocation()
    const params = new URLSearchParams(search)
    const pageFromUrl = parseInt(params.get('page'), 10) || 1
    const searchFromUrl = params.get('title') || ''
    const categoryFromUrl = params.get('category') || ''

    const [products, setProducts] = useState([])
    const [currentPage, setCurrentPage] = useState(pageFromUrl)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState(searchFromUrl)
    const [sortOption, setSortOption] = useState('')
    const [showSort, setShowSort] = useState(false)

    const fetchData = async (page, searchQuery) => {
        setLoading(true)
        try {
            let url = `/product/?page=${page}&limit=9&populate=true`
            if (categoryFromUrl) {
                url += `&category=${categoryFromUrl}`
            }
            if (searchQuery) {
                url += `&title=${encodeURIComponent(searchQuery)}`
            }
            const response = await helpers.httpRequest(url, 'GET')

            if (response.status === 'success') {
                setProducts(response.data || [])
                setTotalPages(response.totalPages || 1)
                setTotal(response.total || 0)
            } else {
                // Clear products when API returns error or no products found
                setProducts([])
                setTotalPages(1)
                setTotal(0)
            }
        } catch (error) {
            console.error('Error fetching Products:', error)
            // Clear products on error
            setProducts([])
            setTotalPages(1)
            setTotal(0)
        } finally {
            setLoading(false)
        }
    }

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            setCurrentPage(1)
            const newParams = new URLSearchParams(search)
            if (searchValue) {
                newParams.set('title', searchValue)
            } else {
                newParams.delete('title')
            }
            newParams.set('page', '1')
            navigate(`/products?${newParams.toString()}`)
            fetchData(1, searchValue)
        }, 500),
        []
    )

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchTerm(value)
        debouncedSearch(value)
    }

    useEffect(() => {
        fetchData(currentPage, searchTerm)
    }, [currentPage])

    // Update URL and state when page changes
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage)
        const newParams = new URLSearchParams(search)
        newParams.set('page', newPage.toString())
        if (categoryFromUrl) newParams.set('category', categoryFromUrl)
        navigate(`/products?${newParams.toString()}`)
        const element = document.querySelector('.as_shop_wrapper')
        if (element) {
            const elementRect = element.getBoundingClientRect()
            const offset = 180
            const scrollToY = window.scrollY + elementRect.top - offset
            window.scrollTo({ top: scrollToY, behavior: 'smooth' })
        }
    }

    // compute sorted products
    const sortedProducts = useMemo(() => {
        let sorted = [...products]
        if (sortOption === 'price-low') sorted.sort((a,b)=>a.price - b.price)
        else if (sortOption === 'price-high') sorted.sort((a,b)=>b.price - a.price)
        else if (sortOption === 'name-asc') sorted.sort((a,b)=>a.title.localeCompare(b.title))
        else if (sortOption === 'name-desc') sorted.sort((a,b)=>b.title.localeCompare(a.title))
        return sorted
    }, [products, sortOption])

    return (
        <>
            <BannerCarousel section="product_offers" />
            <section className="as_shop_wrapper as_padderBottom90 as_padderTop80">
                <div className="container">
                    <div className="d-flex flex-nowrap justify-content-end align-items-center gap-2 mb-3">
                        <div>
                            <button className="btn btn-outline-secondary gradient-hover d-flex align-items-center gap-1" onClick={()=>setShowSort(true)}>
                                <i className="fas fa-sort-amount-down"></i><span className="d-none d-md-inline"> Sort by</span>
                            </button>
                        </div>
                        <div style={{minWidth:200}}>
                            <div className="search-container">
                                <input
                                    className="modern-search-input"
                                    placeholder="Search name..."
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                <button className="modern-search-btn" type="button">
                                    <i className="fas fa-search"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <SortModal show={showSort} onClose={()=>setShowSort(false)} sortOption={sortOption} setSortOption={setSortOption} />
                    <div className="row">
                        {loading ? (
                            <div className="col-12 text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : sortedProducts.length > 0 ? (
                            sortedProducts.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))
                        ) : (
                            <div className="col-12 text-center">
                                <h3>No products found</h3>
                            </div>
                        )}
                    </div>

                    {sortedProducts.length > 0 && (
                        <Pagination
                            page={currentPage}
                            lastPage={totalPages}
                            setPage={handlePageChange}
                            response={{
                                from: (currentPage - 1) * 10 + 1,
                                to: Math.min(currentPage * 10, total),
                                total,
                            }}
                            path='products'
                        />
                    )}
                </div>
            </section>
        </>
    )
}

function SortModal({show, onClose, sortOption, setSortOption}){
    const OPTIONS = [
        { key: 'price-low', label: 'Price: Low to High' },
        { key: 'price-high', label: 'Price: High to Low' },
        { key: 'name-asc', label: 'Name: A to Z' },
        { key: 'name-desc', label: 'Name: Z to A' },
    ]
    return (
        <div className={`filter-modal-backdrop${show?' show':''}`} onClick={onClose}>
            <div className="filter-modal show" onClick={e=>e.stopPropagation()} style={{width:320}}>
                <div className="filter-modal-header d-flex justify-content-between align-items-center">
                    <span className="fw-bold">SORT BY</span>
                    <button className="btn btn-link text-dark fs-4 p-0" onClick={onClose}>&times;</button>
                </div>
                <div className="filter-modal-body" style={{padding:'10px 20px'}}>
                    {OPTIONS.map(opt=> (
                        <div className="form-check mb-2" key={opt.key}>
                            <input type="radio" className="form-check-input" id={`sort-${opt.key}`} name="sortby" checked={sortOption===opt.key} onChange={()=>setSortOption(opt.key)} />
                            <label className="form-check-label" htmlFor={`sort-${opt.key}`}>{opt.label}</label>
                        </div>
                    ))}
                </div>
                <div className="filter-modal-footer d-flex justify-content-between align-items-center mt-3">
                    <button className="btn btn-link text-danger" onClick={()=>{setSortOption(''); onClose();}}>Reset</button>
                    <button className="btn btn-warning px-4" onClick={onClose}>Apply</button>
                </div>
            </div>
        </div>
    )
}

// Inject shared search styles if not already present
if (typeof document !== 'undefined') {
    const styleId = 'astrologer-search-styles'
    const existing = document.getElementById(styleId)
    if (existing) existing.remove()

    const styles = `
        .search-container { display:flex; align-items:center; border:1px solid #6c757d; border-radius:4px; background:#fff; overflow:hidden; }
        .modern-search-input { flex:1; padding:7px 12px; border:none; outline:none; font-size:15px; }
        .modern-search-btn { color:#6c757d; background:transparent; border:none; padding:6px 10px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
        .modern-search-btn:hover { color:#495057; background:#f1f1f1; }

        /* Mobile adjustments */
        @media (max-width: 575.98px) {
            .modern-search-input { padding:5px 10px; font-size:14px; }
            .modern-search-btn { padding:5px 10px; }
        }
    `

    const styleSheet = document.createElement('style')
    styleSheet.type = 'text/css'
    styleSheet.id = styleId
    styleSheet.innerText = styles
    document.head.appendChild(styleSheet)
}

export default ShopComponent
